/**
 * 35 - Beacon (10 §2): carry the strike beacon (it rides in pod A) to the marked point in the middle of the
 * enemy's command compound, set it down, and be 80 m clear when the strike lands a minute later - it flattens
 * everything within 25 m of the mark. Strict quiet until he is inside the wire.
 *
 * The minute is a floor, not a constant: `finish` measures the run from the mark to open ground 80 m off on
 * the finished map, and gives half as long again if that is more (helpers3aRoute.ts).
 */

import { checker, clutter, guardAtDoor, guardsAbout, insideRect, required, ruleOf, structure } from './helpers';
import { lamp, put } from './helpers3a';
import { checkRuleText, checkTargets, timedRules } from './helpers3aAsserts';
import { escapeSeconds, retime, round5 } from './helpers3aRoute';
import type { Point } from '../types';
import type { ArchetypeSpec } from './types';

export const CLEAR_DISTANCE = 80;
export const STRIKE_SECONDS = 60;
export const STRIKE_RADIUS = 25;
/** A fair strike delay: the design's minute, or the run out at a sprint and half as long again. */
export const strikeDelay = (runSeconds: number): number => Math.max(STRIKE_SECONDS, round5(runSeconds * 1.5));

export const beacon: ArchetypeSpec = {
  id: 'beacon',
  name: 'BEACON',
  nativeBiome: 'arctic',
  baseThreat: 5,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [48, 56], d: [32, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const cx = (g.rect.x0 + g.rect.x1) / 2;
    const cz = (g.rect.z0 + g.rect.z1) / 2;
    // The mark first, dead centre, with clear ground round it: that is where the beacon goes.
    const mark = put(g, 'strikeBeacon', cx, cz, { target: true, margin: 2.5 });
    const placed: Point[] = [{ x: mark.x, z: mark.z }];
    // What the strike is for: the command post, the signals hut, quarters, stores - all inside its 25 m.
    const post = required(g, 'commsPost', { near: mark, maxDist: 16, minDist: 7, from: placed, margin: 1.4 });
    const quarters = structure(g, 'hut', { near: mark, maxDist: 18, minDist: 7, from: placed, margin: 1.2 });
    structure(g, g.kit.shelter, { near: mark, maxDist: 18, minDist: 7, from: placed, margin: 1.2 });
    structure(g, 'ammoShack', { near: mark, maxDist: 20, minDist: 9, from: placed, margin: 1.4 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 4);
    if (g.threat >= 4) lamp(g, g.ctx.plan.roadEnd.x + 5, g.inner.z1 - 2);
    guardAtDoor(g, post);
    if (quarters) guardAtDoor(g, quarters);
    guardsAbout(g, 2 + g.rng.int(2));

    const inside = { ...g.rect };
    return {
      goalZone: inside,
      objective: {
        kind: 'reachDistance', label: 'PLACE THE BEACON AND GET CLEAR', targetPropIds: [mark.id], minFraction: 1,
        questItem: { name: 'STRIKE BEACON', weight: 4, pod: 0 }, useLabel: 'PLACE THE BEACON',
        deliverToPropId: mark.id, clearFromPropId: mark.id, clearDistance: CLEAR_DISTANCE,
        rules: [
          { type: 'quietUntilZone', zone: inside, breach: 'penalty' },
          {
            type: 'timedEvent', atSeconds: STRIKE_SECONDS, effect: 'strikeLands', from: 'armed',
            strikeRadius: STRIKE_RADIUS,
            orders: `THE STRIKE LANDS ON THE BEACON ONCE IT IS PLACED: BE ${CLEAR_DISTANCE} M CLEAR OR DIE IN IT`,
            warning: 'STRIKE LANDS IN',
            reason: 'CAUGHT IN THE STRIKE',
          },
          { type: 'noContactBonus' },
          { type: 'recall', mode: 'normal' },
        ],
      },
      finish(def) {
        const run = escapeSeconds(def, mark, CLEAR_DISTANCE);
        if (run !== null) retime(def, (r) => r.effect === 'strikeLands', strikeDelay(run));
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('beacon');
    const [mark] = checkTargets(check, def, ['strikeBeacon']);
    const o = def.objective;
    check(
      o.kind === 'reachDistance' && o.targetPropIds.length === 1,
      'objective must be to place the beacon and get clear',
    );
    check(
      o.deliverToPropId === mark?.id && o.clearFromPropId === mark?.id,
      'the beacon is placed at, and cleared from, the mark',
    );
    check(o.clearDistance === CLEAR_DISTANCE, `he must be ${CLEAR_DISTANCE} m clear`);
    check(o.questItem !== undefined && o.questItem.pod === 0 && o.questItem.weight > 0, 'the beacon rides in pod A');
    check((o.useLabel ?? '').length > 0, 'the Interact prompt needs its words');
    if (mark) {
      const c = def.goalZone;
      const centred = Math.abs(mark.x - (c.x0 + c.x1) / 2) <= 1 && Math.abs(mark.z - (c.z0 + c.z1) / 2) <= 1;
      check(insideRect(c, mark.x, mark.z) && centred, 'the mark is the centre of the compound');
      check(
        probe.standable({ x0: mark.x - 2, z0: mark.z - 2, x1: mark.x + 2, z1: mark.z + 2 }) >= 9,
        'no room to set it down',
      );
      // 80 m clear must be on the map: south of the mark, short of the badlands.
      check(mark.z + CLEAR_DISTANCE + 6 < def.sizeZ, 'nowhere on the map is 80 m clear of the mark');
      const struck = def.props.filter((p) => p.kind === 'commsPost' || p.kind === 'hut' || p.kind === 'ammoShack');
      const hit = (p: { x: number; z: number }): boolean =>
        (p.x - mark.x) * (p.x - mark.x) + (p.z - mark.z) * (p.z - mark.z) <= STRIKE_RADIUS * STRIKE_RADIUS;
      check(struck.some(hit),
        'the strike must have something to hit');
    }
    const [strike] = timedRules(def, 'strikeLands');
    check(
      strike !== undefined && strike.from === 'armed' && strike.strikeRadius === STRIKE_RADIUS,
      'the strike is counted from the placing',
    );
    check(strike !== undefined && strike.atSeconds >= STRIKE_SECONDS && strike.atSeconds <= STRIKE_SECONDS * 2,
      `the strike lands ${STRIKE_SECONDS} s after the placing, or a little later on a map that needs it`);
    check(ruleOf(def, 'quietUntilZone')?.breach === 'penalty', 'strict quiet until inside the compound');
    check(ruleOf(def, 'noContactBonus') !== undefined, 'offers the no-contact bonus');
    checkRuleText(check, def);
    return out;
  },
};
